/**
 * Pure, frame-driven speech/silence detector for 100 ms PCM frames.
 *
 * Every decision is a frame count (1 frame = 100 ms); no wall-clock timers. Feed it one PcmFrame
 * per 100 ms and act on the flags in the returned result. The orchestrator (answerRecorder) owns
 * the side effects; this class never touches the DOM.
 *
 * Behaviour (see scout-capture.md silence_detector_spec):
 *  - warm-up: the first 2 frames are recorded but ignored for floor and speech decisions
 *  - adaptive noise floor initialised from frames 3-5, fast attack downward, slow release upward
 *    (0.3 dB per frame), clamped to [-70, -30]; all-zero frames never feed the floor
 *  - thresholds: onDb = clamp(floor + 12, -55, -20), offDb = onDb - 5 (hysteresis)
 *  - onset after 3 consecutive LOUD frames (300 ms); armed once 700 ms of speech accumulated
 *  - auto-stop when armed and trailing silence >= silenceMs (default 3000); hard cap at maxMs (90 s)
 *  - dead-tap watchdog: 20 consecutive all-zero frames
 *  - level meter: level01 = clamp((rmsDb + 60) / 45, 0, 1) smoothed 0.6 / 0.4
 */

import { FRAME_MS, type PcmFrame } from "./pcmDownsampler";

export type FrameClass = "LOUD" | "MID" | "QUIET";
export type VadState = "SILENCE" | "SPEECH";

export interface SilenceDetectorOptions {
  /** Trailing silence that ends an armed answer. Default 3000 (product rule; 1800-3000 sensible). */
  silenceMs?: number;
  /** Hard cap on answer length. Default 90000. */
  maxMs?: number;
  /** Consecutive LOUD time that forms an onset. Default 300. */
  minSpeechMs?: number;
  /** Cumulative speech before auto-stop may fire. Default 700. */
  armAfterSpeechMs?: number;
  /** When to raise the one-shot "we cannot hear you" hint if not armed. Default 12000. */
  noSpeechHintMs?: number;
  /** Frames ignored after the mic opens. Default 2. */
  warmupFrames?: number;
  /** Frames after warm-up whose minimum rmsDb initialises the floor. Default 3. */
  floorInitFrames?: number;
  /** Consecutive all-zero frames that mark the tap dead. Default 50 (5 s).
   *  A real dead tap never recovers, so a long window costs nothing and keeps
   *  ordinary mid-answer pauses well clear of it. */
  deadTapFrames?: number;
  /** Consecutive all-zero frames that show the "no signal" meter state. Default 5. */
  noSignalFrames?: number;
}

export interface DetectorFrameResult {
  /** Classification of this frame against the current thresholds (QUIET during warm-up). */
  cls: FrameClass;
  /** True for the ignored warm-up frames. */
  warmup: boolean;
  /** True when this frame counts as confirmed speech (onset frame or LOUD/MID while in SPEECH). */
  speech: boolean;
  /** True on the frame that completed an onset (the 3rd consecutive LOUD frame). */
  onset: boolean;
  /** When onset is true: how many EARLIER frames retroactively became speech (minSpeechFrames - 1). */
  onsetBack: number;
  state: VadState;
  armed: boolean;
  speechMs: number;
  trailingSilenceMs: number;
  elapsedMs: number;
  floorDb: number;
  onDb: number;
  offDb: number;
  /** Smoothed 0..1 meter value. */
  level01: number;
  zeroRun: number;
  /** zeroRun >= noSignalFrames: render a distinct "no signal" meter state. */
  noSignal: boolean;
  /** armed && trailingSilenceMs >= silenceMs. */
  autoStop: boolean;
  /** elapsedMs >= maxMs. */
  capReached: boolean;
  /** Fires exactly on the frame where zeroRun reaches deadTapFrames, and only
   *  after speech has been confirmed at least once on this tap. */
  deadTap: boolean;
  /** Fires once, on the first frame at/after noSpeechHintMs while not armed. */
  noSpeechHint: boolean;
}

const FLOOR_MIN = -70;
const FLOOR_MAX = -30;
const ON_MIN = -55;
const ON_MAX = -20;
const ON_OFFSET = 12;
const HYSTERESIS = 5;
const FLOOR_ATTACK = 0.5;
const FLOOR_RELEASE_PER_FRAME = 0.3;
/** Floor assumed until the init frames have been seen (a quiet phone room with AGC). */
const FLOOR_PRIOR = -50;

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

export class SilenceDetector {
  readonly silenceMs: number;
  readonly maxMs: number;
  readonly minSpeechFrames: number;
  readonly armAfterSpeechMs: number;
  readonly noSpeechHintMs: number;
  readonly warmupFrames: number;
  readonly floorInitFrames: number;
  readonly deadTapFrames: number;
  readonly noSignalFrames: number;

  private frames = 0;
  private floorDb = FLOOR_PRIOR;
  private floorInitialised = false;
  private onDb = clamp(FLOOR_PRIOR + ON_OFFSET, ON_MIN, ON_MAX);
  private offDb = clamp(FLOOR_PRIOR + ON_OFFSET, ON_MIN, ON_MAX) - HYSTERESIS;
  private state: VadState = "SILENCE";
  private loudRun = 0;
  private speechMs = 0;
  private trailingSilenceMs = 0;
  private armed = false;
  private zeroRun = 0;
  private meter = 0;
  private hintFired = false;

  constructor(opts: SilenceDetectorOptions = {}) {
    this.silenceMs = opts.silenceMs ?? 3000;
    this.maxMs = opts.maxMs ?? 90000;
    this.minSpeechFrames = Math.max(1, Math.round((opts.minSpeechMs ?? 300) / FRAME_MS));
    this.armAfterSpeechMs = opts.armAfterSpeechMs ?? 700;
    this.noSpeechHintMs = opts.noSpeechHintMs ?? 12000;
    this.warmupFrames = opts.warmupFrames ?? 2;
    this.floorInitFrames = opts.floorInitFrames ?? 3;
    this.deadTapFrames = opts.deadTapFrames ?? 50;
    this.noSignalFrames = opts.noSignalFrames ?? 5;
  }

  /** Consume one 100 ms frame. Only rmsDb and allZero are read. */
  push(frame: Pick<PcmFrame, "rmsDb" | "allZero">): DetectorFrameResult {
    const idx = this.frames++;
    const elapsedMs = this.frames * FRAME_MS;
    const rmsDb = Number.isFinite(frame.rmsDb) ? frame.rmsDb : -180;
    const allZero = !!frame.allZero;

    // Dead-tap watchdog. It may only fire once speech has actually been heard
    // on this tap: before the first word there is no reason to expect audio, so
    // a candidate thinking before they answer is not evidence of a dead
    // microphone — that false positive was demoting working phones to the
    // MediaRecorder tier (no level meter, no auto-submit) mid-interview.
    // A genuinely dead tap stays dead, so nothing real is missed by waiting.
    if (allZero) this.zeroRun += 1;
    else this.zeroRun = 0;
    const deadTap = this.speechMs > 0 && this.zeroRun === this.deadTapFrames;

    // Level meter.
    const raw = clamp((rmsDb + 60) / 45, 0, 1);
    this.meter = 0.6 * this.meter + 0.4 * raw;

    let cls: FrameClass = "QUIET";
    let speech = false;
    let onset = false;
    let onsetBack = 0;
    const warmup = idx < this.warmupFrames;

    if (!warmup) {
      const initPhase = idx < this.warmupFrames + this.floorInitFrames;
      // Floor initialisation: running minimum of the init frames (all-zero frames excluded).
      if (!allZero && (initPhase || !this.floorInitialised)) {
        this.floorDb = this.floorInitialised ? Math.min(this.floorDb, rmsDb) : rmsDb;
        this.floorDb = clamp(this.floorDb, FLOOR_MIN, FLOOR_MAX);
        this.floorInitialised = true;
        this.recomputeThresholds();
      }

      // Classify against the current thresholds.
      if (rmsDb > this.onDb) cls = "LOUD";
      else if (rmsDb < this.offDb) cls = "QUIET";
      else cls = "MID";

      // Adaptive floor (after the init frames; never fed by all-zero frames).
      if (!initPhase && !allZero) {
        if (rmsDb < this.floorDb) this.floorDb += FLOOR_ATTACK * (rmsDb - this.floorDb);
        else if (cls !== "LOUD") this.floorDb = Math.min(rmsDb, this.floorDb + FLOOR_RELEASE_PER_FRAME);
        this.floorDb = clamp(this.floorDb, FLOOR_MIN, FLOOR_MAX);
        this.recomputeThresholds();
      }

      // State machine.
      if (this.state === "SILENCE") {
        if (cls === "LOUD") {
          this.loudRun += 1;
          if (this.loudRun >= this.minSpeechFrames) {
            this.state = "SPEECH";
            this.speechMs += this.minSpeechFrames * FRAME_MS;
            this.trailingSilenceMs = 0;
            this.loudRun = 0;
            speech = true;
            onset = true;
            onsetBack = this.minSpeechFrames - 1;
          }
          // LOUD frames that have not formed an onset neither add to nor reset trailing silence.
        } else {
          this.loudRun = 0;
          this.trailingSilenceMs += FRAME_MS;
        }
      } else {
        if (cls === "QUIET") {
          this.state = "SILENCE";
          this.trailingSilenceMs = FRAME_MS;
          this.loudRun = 0;
        } else {
          this.speechMs += FRAME_MS;
          this.trailingSilenceMs = 0;
          speech = true;
        }
      }

      if (!this.armed && this.speechMs >= this.armAfterSpeechMs) this.armed = true;
    }

    const autoStop = this.armed && this.trailingSilenceMs >= this.silenceMs;
    const capReached = elapsedMs >= this.maxMs;
    let noSpeechHint = false;
    if (!this.hintFired && !this.armed && elapsedMs >= this.noSpeechHintMs) {
      this.hintFired = true;
      noSpeechHint = true;
    }

    return {
      cls,
      warmup,
      speech,
      onset,
      onsetBack,
      state: this.state,
      armed: this.armed,
      speechMs: this.speechMs,
      trailingSilenceMs: this.trailingSilenceMs,
      elapsedMs,
      floorDb: this.floorDb,
      onDb: this.onDb,
      offDb: this.offDb,
      level01: this.meter,
      zeroRun: this.zeroRun,
      noSignal: this.zeroRun >= this.noSignalFrames,
      autoStop,
      capReached,
      deadTap,
      noSpeechHint,
    };
  }

  /** Current values without consuming a frame. */
  snapshot(): Pick<
    DetectorFrameResult,
    "state" | "armed" | "speechMs" | "trailingSilenceMs" | "elapsedMs" | "floorDb" | "onDb" | "offDb" | "level01" | "zeroRun"
  > {
    return {
      state: this.state,
      armed: this.armed,
      speechMs: this.speechMs,
      trailingSilenceMs: this.trailingSilenceMs,
      elapsedMs: this.frames * FRAME_MS,
      floorDb: this.floorDb,
      onDb: this.onDb,
      offDb: this.offDb,
      level01: this.meter,
      zeroRun: this.zeroRun,
    };
  }

  reset(): void {
    this.frames = 0;
    this.floorDb = FLOOR_PRIOR;
    this.floorInitialised = false;
    this.recomputeThresholds();
    this.state = "SILENCE";
    this.loudRun = 0;
    this.speechMs = 0;
    this.trailingSilenceMs = 0;
    this.armed = false;
    this.zeroRun = 0;
    this.meter = 0;
    this.hintFired = false;
  }

  private recomputeThresholds(): void {
    this.onDb = clamp(this.floorDb + ON_OFFSET, ON_MIN, ON_MAX);
    this.offDb = this.onDb - HYSTERESIS;
  }
}
